import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {FILTER_SECTION, FILTER_TYPE} from '../../../../constants';
import {FilterWrapper as FilterV4} from '../../../shared/components/filters/FilterWrapper';
import {FILTER_SECTIONS} from '../../../shared/components/filters/dropdown_section';
import {getFiltersAlphabetically} from '../../../shared/components/filters/filter_util';
import {isClientUser} from '../../../shared/util/PermissionsUtil';
import Util from '../../../shared/util/util';
import type {TimesheetsFilters_viewer$data as TimesheetsFilters_viewer} from './__generated__/TimesheetsFilters_viewer.graphql';
import {EMPHASIS} from '@forecast-it/design-system';
import {readNormalisedStoredFilters} from '../../../shared/components/filters/projectStageFilterNormaliser';

const localStorageKey = 'timesheets-filters';

export type DefaultFiltersType = {
	task: {
		person?: string[] | null;
		statusCategory?: string[] | null;
	};
};

const getTaskFilters = () => {
	const taskFilters = [
		FILTER_TYPE.PROJECT,
		FILTER_TYPE.STATUS_CATEGORY,
		FILTER_TYPE.DEADLINE,
		FILTER_TYPE.DEPENDENCIES,
		FILTER_TYPE.FOLLOWER,
		FILTER_TYPE.INDICATOR_FILTERED,
		FILTER_TYPE.LABEL,
		FILTER_TYPE.PHASE_CATEGORY,
		FILTER_TYPE.OWNER,
		FILTER_TYPE.RECENT_ACTIVITY,
		FILTER_TYPE.SPRINT_CATEGORY,
		FILTER_TYPE.PROJECT_STAGE,
		FILTER_TYPE.TASK_HIERARCHY,
		FILTER_TYPE.PERSON,
		FILTER_TYPE.TASK_PROJECT_LABEL,
		FILTER_TYPE.ALL_PROJECT_PHASES,
		FILTER_TYPE.RECENT_TASK_ACTIVITY,
	];
	if (!isClientUser()) {
		taskFilters.push(FILTER_TYPE.TEAM);
		taskFilters.push(FILTER_TYPE.CLIENT);
	}
	return taskFilters;
};

const getPrimaryFilters = () => {
	const primaryFilters = {
		[FILTER_SECTIONS.TASKS]: [
			/*FIXME: If we want to bring back the deadline filter we need to address how it interacts with the selected day / week
      Currently we hardcode a start and end date filter based on the calendar selection and offer no way of disabling those
      Since the entire tab is labeled "this weeks work" it also makes sense to keep the date filter hardcoded
      FILTER_TYPE.DEADLINE, */
			FILTER_TYPE.CLIENT,
			FILTER_TYPE.PROJECT,
			FILTER_TYPE.ALL_PROJECT_PHASES,
			FILTER_TYPE.STATUS_CATEGORY,
			FILTER_TYPE.PERSON,
		],
	};

	return primaryFilters;
};

const getDefaultFilters = (personId: string): DefaultFiltersType => {
	const defaultFilers = {task: {person: [personId], statusCategory: ['INPROGRESS']}};
	Util.localStorageSetItem(localStorageKey, JSON.stringify(defaultFilers));
	return defaultFilers;
};

export const getInitialState = (personId: string): DefaultFiltersType => {
	let localStorageFilters: DefaultFiltersType | undefined = undefined;
	try {
		// Temporary: remove with the custom_project_stages flag clean-up
		// Stored filters may still hold stage profile names in projectStage
		localStorageFilters = (readNormalisedStoredFilters(localStorageKey) as DefaultFiltersType | null) ?? undefined;
	} catch (e) {
		console.error(e);
	}
	if (localStorageFilters) {
		return localStorageFilters;
	}
	return getDefaultFilters(personId);
};

interface TimesheetsFiltersProps {
	viewer: TimesheetsFilters_viewer;
	filters: DefaultFiltersType;
	setFilters: (filters) => void;
	setSavedFilters: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
	emphasis?: EMPHASIS;
}

// FIXME: Exported directly to avoid Relay fragment error. Once timesheets_page is transitioned to TSX, it should start using the fragment instead.
export const TimesheetsFilters = ({
	filters,
	setFilters,
	setSavedFilters,
	viewer,
	emphasis,
}: TimesheetsFiltersProps): React.ReactElement => {
	const {formatMessage} = useIntl();

	const taskFilters = useMemo(getTaskFilters, []);
	const primaryFilters = useMemo(getPrimaryFilters, []);

	const onFiltersChange = filters => {
		Util.localStorageSetItem(localStorageKey, JSON.stringify(filters));
		setFilters(filters);
	};

	return (
		<FilterV4
			taskFilters={getFiltersAlphabetically(taskFilters, formatMessage)}
			primaryFilters={primaryFilters}
			defaultSection={FILTER_SECTIONS.TASKS}
			filterSection={FILTER_SECTION.MY_TIMESHEETS}
			onFiltersChange={onFiltersChange}
			appliedFiltersName={localStorageKey}
			iconOnly={undefined}
			noMenu={undefined}
			operatorOptions={undefined}
			timeRegFilters={undefined}
			invoiceFilters={undefined}
			peopleFilters={undefined}
			projectFilters={undefined}
			skillFilters={undefined}
			labelFilters={undefined}
			placeholderFilters={undefined}
			resourceFilters={undefined}
			useSavedReport={undefined}
			preAppliedFilters={filters}
			controlledFiltersObject={filters}
			projectId={undefined}
			projectGroupId={undefined}
			programPrefix={undefined}
			companyProjectGroupId={undefined}
			userpilot={undefined}
			cy={'timesheet-filters'}
			useDesignSystemButton={true}
			filterExpandedKey={'-timesheets-filters'}
			viewer={viewer}
			setSavedFilters={setSavedFilters}
			hideSavedFiltersSlider={true}
			emphasis={emphasis}
		/>
	);
};

//FIXME: this fragment should be added to timesheets_page and TimesheetsBottomSection when timesheets_page is converted to TS
export default createFragmentContainer(TimesheetsFilters, {
	viewer: graphql`
		fragment TimesheetsFilters_viewer on Viewer {
			id
			calendarIntegrations {
				googleCalendar {
					calendarFetchEnabled
				}
				outlookCalendar {
					calendarFetchEnabled
				}
			}
			filters(first: 1000000, filterSection: MY_TIMESHEETS) @connection(key: "Viewer_filters", filters: []) {
				edges {
					node {
						id
						name
						section
						value
						updatedAt
					}
				}
			}
		}
	`,
});
